import { useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Modal } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/utils/dashboard/apiError';
import type { RateCard } from '@/types/dashboard/rate';
import { useVendors } from '../VendorsPage/useVendors';
import { useCreateRate, useUpdateRate } from './useRates';

interface FormValues {
  vendor: string;
  bw_rate_per_page: string;
  color_rate_per_page: string;
  duplex_discount_pct: string; // shown as %, sent as a fraction
  merchant_fees: string;
  convenience_fee: string;
  is_active: boolean;
}
type FormErrors = Partial<Record<keyof FormValues, string>>;

interface Props {
  rate: RateCard | null; // null = onboard new rate card
  onClose: () => void;
}

const isMoney = (s: string) => /^\d{1,4}(\.\d{1,2})?$/.test(s.trim());
const toPercent = (fraction: string) => String(Math.round(Number(fraction) * 10000) / 100);
const toFraction = (pct: string) => (Number(pct) / 100).toFixed(2);

function validate(v: FormValues): FormErrors {
  const e: FormErrors = {};
  if (!v.vendor) e.vendor = 'Select a vendor';
  if (!isMoney(v.bw_rate_per_page)) e.bw_rate_per_page = 'Enter a valid rate (max 2 decimals)';
  if (!isMoney(v.color_rate_per_page)) e.color_rate_per_page = 'Enter a valid rate (max 2 decimals)';
  if (!isMoney(v.merchant_fees)) e.merchant_fees = 'Enter a valid amount';
  if (!isMoney(v.convenience_fee)) e.convenience_fee = 'Enter a valid amount';
  const pct = Number(v.duplex_discount_pct);
  if (v.duplex_discount_pct.trim() === '' || Number.isNaN(pct) || pct < 0 || pct > 100) {
    e.duplex_discount_pct = 'Enter a percentage between 0 and 100';
  }
  return e;
}

function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string; children: ReactNode }) {
  return (
    <div className="mb-3">
      <label className="text-label-md text-on-surface mb-1 d-block">{label}</label>
      {children}
      {hint && !error && <div className="text-body-sm text-on-surface-variant mt-1">{hint}</div>}
      {error && <div className="text-danger text-body-sm mt-1">{error}</div>}
    </div>
  );
}

export function RateFormModal({ rate, onClose }: Props) {
  const isEdit = rate !== null;
  const { data: vendors = [] } = useVendors();
  const createRate = useCreateRate();
  const updateRate = useUpdateRate();

  const [values, setValues] = useState<FormValues>({
    vendor: rate ? String(rate.vendor) : '',
    bw_rate_per_page: rate?.bw_rate_per_page ?? '3.00',
    color_rate_per_page: rate?.color_rate_per_page ?? '5.00',
    duplex_discount_pct: rate ? toPercent(rate.duplex_extra_charge) : '0',
    merchant_fees: rate?.merchant_fees ?? '0.00',
    convenience_fee: rate?.convenience_fee ?? '0.00',
    is_active: rate?.is_active ?? true,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const set = <K extends keyof FormValues>(key: K, val: FormValues[K]) =>
    setValues((p) => ({ ...p, [key]: val }));
  const invalid = (k: keyof FormValues) => (errors[k] ? ' is-invalid' : '');

  // Mirrors RateCharge.calculate() on the backend, for a 10-page sample.
  const preview = useMemo(() => {
    if (Object.keys(validate(values)).length > 0) return null;
    const pages = 10;
    const bw = (Number(values.bw_rate_per_page) + Number(values.merchant_fees)) * pages;
    const color = (Number(values.color_rate_per_page) + Number(values.merchant_fees)) * pages;
    const disc = 1 - Number(values.duplex_discount_pct) / 100;
    const fee = Number(values.convenience_fee);
    return { bw: bw + fee, color: color + fee, bwDuplex: bw * disc + fee, colorDuplex: color * disc + fee };
  }, [values]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const errs = validate(values);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitError('');
    setSubmitting(true);
    try {
      const payload = {
        vendor: Number(values.vendor),
        bw_rate_per_page: values.bw_rate_per_page.trim(),
        color_rate_per_page: values.color_rate_per_page.trim(),
        duplex_extra_charge: toFraction(values.duplex_discount_pct),
        merchant_fees: values.merchant_fees.trim(),
        convenience_fee: values.convenience_fee.trim(),
        is_active: values.is_active,
      };
      if (rate) {
        await updateRate.mutateAsync({ id: rate.id, payload });
        toast.success('Rate card updated');
      } else {
        await createRate.mutateAsync(payload);
        toast.success('Rate card created');
      }
      onClose();
    } catch (err) {
      setSubmitError(getApiErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const money = (n: number) => `₹${n.toFixed(2)}`;

  return (
    <Modal show onHide={submitting ? undefined : onClose} centered size="lg" backdrop="static">
      <form onSubmit={onSubmit} noValidate>
        <Modal.Header closeButton={!submitting}>
          <Modal.Title className="text-headline-sm">{isEdit ? 'Edit rate card' : 'Onboard rate card'}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Field label="Vendor" error={errors.vendor}>
            <select
              className={`form-select${invalid('vendor')}`}
              value={values.vendor}
              onChange={(e) => set('vendor', e.target.value)}
              disabled={isEdit}
            >
              <option value="">Select vendor…</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </Field>

          <div className="row">
            <div className="col-md-6">
              <Field label="B/W rate per page (₹)" error={errors.bw_rate_per_page}>
                <input
                  className={`form-control${invalid('bw_rate_per_page')}`}
                  inputMode="decimal"
                  value={values.bw_rate_per_page}
                  onChange={(e) => set('bw_rate_per_page', e.target.value)}
                />
              </Field>
            </div>
            <div className="col-md-6">
              <Field label="Colour rate per page (₹)" error={errors.color_rate_per_page}>
                <input
                  className={`form-control${invalid('color_rate_per_page')}`}
                  inputMode="decimal"
                  value={values.color_rate_per_page}
                  onChange={(e) => set('color_rate_per_page', e.target.value)}
                />
              </Field>
            </div>
            <div className="col-md-4">
              <Field
                label="Duplex discount (%)"
                hint="Off the printing amount for double-sided jobs"
                error={errors.duplex_discount_pct}
              >
                <input
                  className={`form-control${invalid('duplex_discount_pct')}`}
                  inputMode="decimal"
                  value={values.duplex_discount_pct}
                  onChange={(e) => set('duplex_discount_pct', e.target.value)}
                />
              </Field>
            </div>
            <div className="col-md-4">
              <Field label="Merchant fee (₹/page)" hint="Added to every page, B/W and colour" error={errors.merchant_fees}>
                <input
                  className={`form-control${invalid('merchant_fees')}`}
                  inputMode="decimal"
                  value={values.merchant_fees}
                  onChange={(e) => set('merchant_fees', e.target.value)}
                />
              </Field>
            </div>
            <div className="col-md-4">
              <Field label="Convenience fee (₹)" hint="Flat, once per print request" error={errors.convenience_fee}>
                <input
                  className={`form-control${invalid('convenience_fee')}`}
                  inputMode="decimal"
                  value={values.convenience_fee}
                  onChange={(e) => set('convenience_fee', e.target.value)}
                />
              </Field>
            </div>
          </div>

          {preview && (
            <div className="bg-surface-low rounded-3 p-3 mb-3">
              <div className="text-label-md text-on-surface mb-1">Customer price for a 10-page job</div>
              <div className="text-body-sm text-on-surface-variant">
                B/W {money(preview.bw)} · B/W duplex {money(preview.bwDuplex)} · Colour {money(preview.color)} · Colour
                duplex {money(preview.colorDuplex)}
              </div>
            </div>
          )}

          <div className="form-check form-switch">
            <input
              id="rate-active"
              type="checkbox"
              className="form-check-input"
              checked={values.is_active}
              onChange={(e) => set('is_active', e.target.checked)}
            />
            <label className="form-check-label text-label-md" htmlFor="rate-active">
              Active
            </label>
          </div>

          {submitError && (
            <div className="alert alert-danger d-flex gap-2 mt-3 mb-0 py-2" role="alert">
              <i className="bi bi-exclamation-circle" />
              <span className="text-body-sm">{submitError}</span>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Saving…
              </>
            ) : isEdit ? (
              'Save changes'
            ) : (
              'Create rate card'
            )}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
